import PreviewMedia from '../../widgets/components/PreviewMedia';
import { indexAfterRemoval } from '../../shared/utils/media';
import { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import DeleteIcon from '@mui/icons-material/Delete';
import {
    Box,
    Typography,
    CircularProgress,
    Alert,
    TextField,
    Button,
    FormControlLabel,
    Checkbox,
    IconButton,
} from '@mui/material';
import { useSwipeable } from 'react-swipeable';
import api from '../../api/axios';

type MediaPreview = {
    file?: File;
    originalPath?: string;
    src: string;
};

const seasonOptions = ['Зима', 'Лето'];

const TyreDetailPage = () => {
    const { id } = useParams<{ id: string }>();
    const navigate = useNavigate();

    const [loading, setLoading] = useState(true);
    const [loadError, setLoadError] = useState('');
    const [saveError, setSaveError] = useState('');
    const [saving, setSaving] = useState(false);

    const [form, setForm] = useState({
        name: '',
        brand: '',
        model: '',
        width: '',
        height: '',
        diameter: '',
        type: '',
        season: '',
        speedIndex: '',
        loadIndex: '',
        thorn: false,
        runflat: false,
        stock: '',
        price: '',
        price_rrp: '',
        article: '',
        isMy: false,
        bestseller: false
    });

    const [errors, setErrors] = useState<{ name?: string }>({});
    const [files, setFiles] = useState<File[]>([]);
    const [previews, setPreviews] = useState<MediaPreview[]>([]);
    const [selectedIndex, setSelectedIndex] = useState(0);
    const [previewIndex, setPreviewIndex] = useState(0);
    const [deletedMedia, setDeletedMedia] = useState<string[]>([]);

    useEffect(() => {
        const fetchTyre = async () => {
            try {
                setLoading(true);
                const res = await api.get(`/api/admin/tyre/${id}`);
                const tyre = res.data;

                setForm({
                    name: tyre.name || '',
                    brand: tyre.brand || '',
                    model: tyre.model || '',
                    width: tyre.width || '',
                    height: tyre.height || '',
                    diameter: tyre.diameter || '',
                    type: tyre.type || '',
                    season: tyre.season || '',
                    speedIndex: tyre.speedIndex || '',
                    loadIndex: tyre.loadIndex || '',
                    thorn: Boolean(tyre.thorn),
                    runflat: Boolean(tyre.runflat),
                    stock: tyre.stock != null ? String(tyre.stock) : '',
                    price: tyre.price != null ? String(tyre.price) : '',
                    price_rrp: tyre.price_rrp != null ? String(tyre.price_rrp) : '',
                    article: tyre.article || '',
                    isMy: Boolean(tyre.isMy),
                    bestseller: Boolean(tyre.bestseller) || false
                });

                const prod = process.env.REACT_APP_PROD ? true : false;
                const API_URL = prod ? '' : 'http://localhost:8833';

                if (tyre.media && Array.isArray(tyre.media)) {
                    const mediaPreviews: MediaPreview[] = tyre.media.map((path: string) => {
                        const full = path.startsWith('http') ? path : `${API_URL}/${path}`;
                        return { src: full, originalPath: path };
                    });

                    setPreviews(mediaPreviews);

                    if (tyre.preview) {
                        const previewUrl = tyre.preview.startsWith('http')
                            ? tyre.preview
                            : `${API_URL}/${tyre.preview}`;

                        const foundIndex = mediaPreviews.findIndex((mp) => mp.src === previewUrl);

                        if (foundIndex !== -1) {
                            setPreviewIndex(foundIndex);
                            setSelectedIndex(foundIndex);
                        } else {
                            setPreviewIndex(0);
                            setSelectedIndex(0);
                        }
                    } else {
                        setPreviewIndex(0);
                        setSelectedIndex(0);
                    }
                } else {
                    setPreviews([]);
                }
            } catch (err) {
                console.error('Ошибка загрузки шины:', err);
                setLoadError('Ошибка при загрузке шины');
            } finally {
                setLoading(false);
            }
        };

        if (id) fetchTyre();
    }, [id]);

    const handleChange = (field: string, value: string | boolean) => {
        setForm((prev) => ({ ...prev, [field]: value }));
    };

    const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        if (e.target.files) {
            const newFiles = Array.from(e.target.files);
            const newPreviews: MediaPreview[] = newFiles.map((file) => ({
                src: URL.createObjectURL(file),
                file,
            }));

            setFiles((prev) => [...prev, ...newFiles]);
            setPreviews((prev) => [...prev, ...newPreviews]);
        }
    };

    const handleRemoveFile = (index: number) => {
        const removed = previews[index];
        if (removed.originalPath) setDeletedMedia(prev => [...prev, removed.originalPath!]);
        setFiles(prev => prev.filter(file => file !== removed.file));
        setPreviews(prev => prev.filter((_, i) => i !== index));
        setPreviewIndex(current => indexAfterRemoval(current, index, previews.length));
        setSelectedIndex(current => indexAfterRemoval(current, index, previews.length));
    };

    const swipeHandlers = useSwipeable({
        onSwipedLeft: () => {
            if (selectedIndex < previews.length - 1) {
                setSelectedIndex(selectedIndex + 1);
            }
        },
        onSwipedRight: () => {
            if (selectedIndex > 0) {
                setSelectedIndex(selectedIndex - 1);
            }
        },
        trackMouse: true,
    });

    const handleSubmit = async () => {
        if (!form.name.trim()) {
            setErrors({ name: 'Наименование обязательно' });
            return;
        }

        setSaving(true);
        setSaveError('');

        const formData = new FormData();

        Object.entries(form).forEach(([key, value]) => {
            formData.append(key, String(value));
        });

        formData.append('previewIndex', String(previewIndex));

        files.forEach((file) => formData.append('media', file));

        deletedMedia.forEach((path) => formData.append('deletedMedia', path));

        try {
            await api.put(`/api/admin/tyre/${id}`, formData, {
                headers: { 'Content-Type': 'multipart/form-data' },
            });
            navigate('/tyre');
        } catch (err) {
            console.error('Ошибка при сохранении шины:', err);
            setSaveError('Ошибка при сохранении шины');
        } finally {
            setSaving(false);
        }
    };

    if (loading) {
        return (
            <Box p={4}>
                <CircularProgress />
            </Box>
        );
    }

    if (loadError) {
        return (
            <Box p={4}>
                <Alert severity="error">{loadError}</Alert>
                <Button sx={{ mt: 2 }} onClick={() => navigate(-1)}>
                    Назад
                </Button>
            </Box>
        );
    }

    return (
        <Box p={4}>
            <Button variant="outlined" onClick={() => navigate(-1)} sx={{ mb: 2 }}>
                ← Назад
            </Button>

            <Typography variant="h5" gutterBottom>
                Редактирование шины
            </Typography>

            {saveError && (
                <Alert severity="error" sx={{ mb: 2 }}>
                    {saveError}
                </Alert>
            )}

            <Box component="form" sx={{ display: 'grid', gap: 2, maxWidth: 600 }}>
                <TextField
                    label="Наименование"
                    value={form.name}
                    onChange={(e) => {
                        setErrors({});
                        handleChange('name', e.target.value);
                    }}
                    error={Boolean(errors.name)}
                    helperText={errors.name}
                    fullWidth
                    required
                />

                <TextField
                    label="Бренд"
                    value={form.brand}
                    onChange={(e) => handleChange('brand', e.target.value)}
                    fullWidth
                />

                <TextField
                    label="Модель"
                    value={form.model}
                    onChange={(e) => handleChange('model', e.target.value)}
                    fullWidth
                />

                <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 2 }}>
                    <TextField
                        label="Ширина"
                        value={form.width}
                        onChange={(e) => handleChange('width', e.target.value)}
                    />
                    <TextField
                        label="Высота"
                        value={form.height}
                        onChange={(e) => handleChange('height', e.target.value)}
                    />
                    <TextField
                        label="Диаметр"
                        value={form.diameter}
                        onChange={(e) => handleChange('diameter', e.target.value)}
                    />
                </Box>

                <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2 }}>
                    <TextField
                        label="Индекс скорости"
                        value={form.speedIndex}
                        onChange={(e) => handleChange('speedIndex', e.target.value)}
                    />
                    <TextField
                        label="Индекс нагрузки"
                        value={form.loadIndex}
                        onChange={(e) => handleChange('loadIndex', e.target.value)}
                    />
                </Box>

                <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2 }}>
                    <TextField
                        label="Тип (Зима/Лето)"
                        value={form.type}
                        onChange={(e) => handleChange('type', e.target.value)}
                        disabled={true}
                    />
                    <TextField
                        select
                        SelectProps={{ native: true }}
                        label="Сезон"
                        value={form.season}
                        onChange={
                            (e) => {
                                handleChange('season', e.target.value)
                                handleChange('type', e.target.value)
                            }
                        }
                    >
                        <option value=""></option>
                        {seasonOptions.map((s) => (
                            <option key={s} value={s}>
                                {s}
                            </option>
                        ))}
                    </TextField>
                </Box>

                <Box display="flex" gap={2}>
                    <FormControlLabel
                        control={
                            <Checkbox
                                checked={form.thorn}
                                onChange={(e) => handleChange('thorn', e.target.checked)}
                            />
                        }
                        label="Шипованные"
                    />
                    <FormControlLabel
                        control={
                            <Checkbox
                                checked={form.runflat}
                                onChange={(e) => handleChange('runflat', e.target.checked)}
                            />
                        }
                        label="RunFlat"
                    />
                                        <FormControlLabel
                        control={
                            <Checkbox
                                checked={form.bestseller}
                                onChange={(e) => handleChange('bestseller', e.target.checked)}
                            />
                        }
                        label="Хит продаж"
                    />
                </Box>

                <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2 }}>
                    <TextField
                        label="Остаток"
                        type="number"
                        value={form.stock}
                        onChange={(e) => handleChange('stock', e.target.value)}
                    />
                    <TextField
                        label="Артикул"
                        value={form.article}
                        onChange={(e) => handleChange('article', e.target.value)}
                    />
                </Box>

                <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2 }}>
                    <TextField
                        label="Цена"
                        type="number"
                        value={form.price}
                        onChange={(e) => handleChange('price', e.target.value)}
                    />
                    <TextField
                        label="РРЦ"
                        type="number"
                        value={form.price_rrp}
                        onChange={(e) => handleChange('price_rrp', e.target.value)}
                    />
                </Box>

                <FormControlLabel
                    control={
                        <Checkbox
                            checked={form.isMy}
                            onChange={(e) => handleChange('isMy', e.target.checked)}
                        />
                    }
                    label="Моя шина"
                />

                <Button variant="contained" component="label">
                    Загрузить фото
                    <input type="file" hidden multiple onChange={handleFileChange} />
                </Button>

                {previews.length > 0 && (
                    <Box display="flex" justifyContent="center" mt={2}>
                        <Box sx={{ position: 'relative' }}>
                            <Box
                                sx={{
                                    width: 300,
                                    height: 200,
                                    overflow: 'hidden',
                                    borderRadius: 2,
                                    position: 'relative',
                                }}
                                {...swipeHandlers}
                            >
                                <PreviewMedia
                                    src={previews[selectedIndex].src}
                                    mimeType={previews[selectedIndex].file?.type}
                                    alt={`preview-${selectedIndex}`}
                                    style={{
                                        width: '100%',
                                        height: '100%',
                                        objectFit: 'contain',
                                        border:
                                            selectedIndex === previewIndex
                                                ? '3px solid #1976d2'
                                                : '1px solid #ccc',
                                        borderRadius: 8,
                                    }}
                                />

                                <IconButton
                                    onClick={() => handleRemoveFile(selectedIndex)}
                                    sx={{
                                        position: 'absolute',
                                        top: 8,
                                        right: 8,
                                        bgcolor: 'white',
                                        color: 'red',
                                        '&:hover': { bgcolor: '#ffe5e5' },
                                    }}
                                >
                                    <DeleteIcon />
                                </IconButton>
                            </Box>

                            <Box
                                sx={{
                                    display: 'flex',
                                    justifyContent: 'center',
                                    alignItems: 'center',
                                    gap: 1,
                                    mt: 1,
                                }}
                            >
                                <Button
                                    variant="outlined"
                                    onClick={() =>
                                        setSelectedIndex((prev) => Math.max(0, prev - 1))
                                    }
                                    disabled={selectedIndex === 0}
                                >
                                    ←
                                </Button>

                                <Typography variant="caption">
                                    {selectedIndex + 1} / {previews.length}
                                </Typography>

                                <Button
                                    variant="outlined"
                                    onClick={() =>
                                        setSelectedIndex((prev) =>
                                            Math.min(previews.length - 1, prev + 1)
                                        )
                                    }
                                    disabled={selectedIndex === previews.length - 1}
                                >
                                    →
                                </Button>
                            </Box>
                        </Box>
                    </Box>
                )}

                {previews.length > 0 && (
                    <Box display="flex" justifyContent="center" gap={2} alignItems="center" mt={1}>
                        <Typography variant="caption">
                            {selectedIndex + 1} / {previews.length}
                        </Typography>

                        <Button
                            variant={selectedIndex === previewIndex ? 'contained' : 'outlined'}
                            size="small"
                            onClick={() => setPreviewIndex(selectedIndex)}
                        >
                            {selectedIndex === previewIndex
                                ? 'Главное изображение'
                                : 'Сделать главным'}
                        </Button>
                    </Box>
                )}

                <Button
                    variant="contained"
                    color="primary"
                    onClick={handleSubmit}
                    disabled={saving}
                >
                    Сохранить изменения
                </Button>
            </Box>
        </Box>
    );
};

export default TyreDetailPage;
